/**
 * SISTEMA: PGLP | MONTE HERMOM
 * COMPONENTE: LaudoFechamento_lacre
 * LOCALIZAÇÃO: /src/component/laudo/
 * STATUS: 100% INTEGRAL - LACRE SOBERANO SAAS
 */

import React, { useState, useEffect } from 'react';
import { 
    Lock, CloudUpload, CheckCircle, FileText, 
    ShieldAlert, History, Loader2, ExternalLink, AlertTriangle 
} from 'lucide-react';
import { executarCustodiaCloud, verificarCotaSovereign } from './LaudoGoogleDriveService_Soberano';


export default function LaudoFechamento_lacre({ laudoFinal, requerente, contextoPerito }) {
    const [status, setStatus] = useState('IDLE'); // IDLE | SYNCING | COMPLETED | ERROR
    const [linkDrive, setLinkDrive] = useState(null);
    const [alertaEspaco, setAlertaEspaco] = useState(null);

    // Checagem de saúde do armazenamento ao carregar
    useEffect(() => {
        verificarCotaSovereign().then(data => {
            if (data && data.remainingPercent < 20) {
                setAlertaEspaco(`Aviso: Drive pglaudopericial com apenas ${data.remainingPercent}% livre.`);
            }
        });
    }, []);

    const dispararLacreSaaS = async () => {
        setStatus('SYNCING');
        
        // Geração do Hash de Integridade (DNA do Laudo)
        const hashBase = JSON.stringify(laudoFinal) + new Date().toISOString();
        const hashSoberano = btoa(hashBase).substring(0, 32);
        const laudoComHash = { ...laudoFinal, hash: hashSoberano };

        const resultado = await executarCustodiaCloud(laudoComHash, requerente, contextoPerito);

        if (resultado.success) {
            setLinkDrive(resultado.driveLink);
            setStatus('COMPLETED');
        } else {
            setStatus('ERROR');
            console.error("Falha no Lacre Digital.");
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-12 py-10 animate-in slide-in-from-bottom-10 duration-1000">
            
            {/* CARD DE LACRE */}
            <div className={`
                bg-white rounded-[5rem] p-20 border-[15px] shadow-luxury text-center transition-all duration-700 
                ${status === 'COMPLETED' ? 'border-green-500' : 'border-slate-900'}
            `}>
                
                {/* ÍCONE DINÂMICO */}
                <div className="flex justify-center mb-10">
                    <div className={`
                        w-32 h-32 rounded-full flex items-center justify-center transition-all 
                        ${status === 'COMPLETED' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-900'}
                    `}>
                        {status === 'IDLE' && <Lock size={50} />}
                        {status === 'SYNCING' && <Loader2 size={50} className="animate-spin" />}
                        {status === 'COMPLETED' && <CheckCircle size={60} />}
                        {status === 'ERROR' && <ShieldAlert size={50} className="text-red-600" />}
                    </div>
                </div>

                <h2 className="text-4xl font-black uppercase italic tracking-tighter text-slate-900 mb-4">
                    {status === 'COMPLETED' ? 'Soberania Confirmada' : 'Lacre Digital do Laudo'}
                </h2>
                
                <p className="text-[11px] font-black text-slate-400 uppercase tracking-[0.4em] mb-12 leading-relaxed italic">
                    {status === 'COMPLETED' 
                        ? `Protocolado em: ${new Date().toLocaleString('pt-BR')}` 
                        : 'Aguardando Assinatura Marcus Ferraz para Custódia SaaS'}
                </p>

                {/* PAINEL DE DADOS DO PROTOCOLO */}
                <div className="bg-slate-900 rounded-[3rem] p-12 text-left space-y-6 mb-12">
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                        <span className="text-[9px] font-black text-blue-400 uppercase">Perito Responsável</span>
                        <span className="text-white font-black italic">{contextoPerito?.nomePerito || "MARCUS FERRAZ"}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                        <span className="text-[9px] font-black text-blue-400 uppercase">Norma Aplicada</span>
                        <span className="text-white font-black italic">{contextoPerito?.normaAplicada || "NBR 14653-2"}</span>
                    </div>
                    <div className="flex justify-between items-center">
                        <span className="text-[9px] font-black text-blue-400 uppercase">ID de Custódia</span>
                        <span className="text-white font-black italic tracking-widest">
                            {status === 'COMPLETED' ? `MH-2026-${laudoFinal.hash}` : 'AGUARDANDO LACRE...'}
                        </span>
                    </div>
                </div>

                {/* BOTÃO DE AÇÃO */}
                <div className="space-y-6">
                    <button 
                        disabled={status !== 'IDLE'}
                        onClick={dispararLacreSaaS}
                        className={`
                            w-full py-12 rounded-[3.5rem] font-black text-sm uppercase tracking-[0.5em] shadow-2xl transition-all flex items-center justify-center gap-6 
                            ${status === 'COMPLETED' ? 'bg-green-500 text-white' : 'bg-slate-900 text-white hover:bg-blue-600'}
                            ${status === 'ERROR' ? 'bg-red-600 text-white' : ''}
                        `}
                    >
                        {status === 'IDLE' && <><CloudUpload size={24} /> ASSINAR E SUBIR PARA O DRIVE</>}
                        {status === 'SYNCING' && <><Loader2 size={24} className="animate-spin" /> SINCRONIZANDO DRIVE...</>}
                        {status === 'COMPLETED' && <><FileText size={24} /> LAUDO CUSTODIADO NO DRIVE</>}
                        {status === 'ERROR' && "FALHA NA CONEXÃO - TENTAR NOVAMENTE"}
                    </button>

                    {status === 'COMPLETED' && (
                        <a 
                            href={linkDrive} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-3 text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline cursor-pointer"
                        >
                            <ExternalLink size={16} /> Abrir Pasta do Processo no Google Drive
                        </a>
                    )}
                </div>
            </div>

            {/* ALERTAS DE MANUTENÇÃO */}
            {alertaEspaco && (
                <div className="bg-amber-50 border-2 border-amber-200 p-6 rounded-[2rem] flex items-center gap-4 text-amber-800">
                    <AlertTriangle size={24} />
                    <span className="text-[10px] font-black uppercase tracking-widest">{alertaEspaco}</span>
                </div>
            )}

            <div className="flex justify-center items-center gap-6 opacity-40 italic">
                <History size={20} className="text-slate-900" />
                <span className="text-[9px] font-black uppercase tracking-[0.6em] text-slate-900 text-center">
                    MANUTENÇÃO DE ARQUIVO: 10 ANOS DE CUSTÓDIA GARANTIDOS
                </span>
            </div>
        </div>
    );
}